import React from 'react';

interface SkeletonDetailProps {
  variant?: 'campaign' | 'influencer';
}

export const SkeletonDetail: React.FC<SkeletonDetailProps> = ({ variant = 'campaign' }) => {
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Button Skeleton */}
        <div className="h-6 w-40 bg-gray-200 rounded mb-6 animate-pulse"></div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {/* Header Section Skeleton */}
          <div className={`px-8 py-6 ${
            variant === 'campaign' 
              ? 'bg-gradient-to-r from-blue-600 to-purple-600' 
              : 'bg-gradient-to-r from-purple-600 to-pink-600'
          }`}>
            {variant === 'campaign' ? (
              <div className="animate-pulse">
                <div className="h-6 w-24 bg-white bg-opacity-20 rounded-full mb-3"></div>
                <div className="h-8 w-3/4 bg-white bg-opacity-20 rounded mb-2"></div>
                <div className="h-4 w-48 bg-white bg-opacity-20 rounded"></div>
              </div>
            ) : (
              <div className="flex items-start space-x-6 animate-pulse">
                <div className="w-32 h-32 bg-white bg-opacity-20 rounded-full flex-shrink-0"></div>
                <div className="flex-1">
                  <div className="h-8 w-48 bg-white bg-opacity-20 rounded mb-2"></div>
                  <div className="h-6 w-32 bg-white bg-opacity-20 rounded-full mb-3"></div>
                  <div className="h-5 w-64 bg-white bg-opacity-20 rounded"></div>
                </div>
              </div>
            )}
          </div>

          {/* Content Section Skeleton */}
          <div className="p-8">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Main Content */}
              <div className="lg:col-span-2 space-y-8 animate-pulse">
                {/* Section 1 */}
                <div>
                  <div className="h-6 w-48 bg-gray-200 rounded mb-4"></div>
                  <div className="space-y-2">
                    <div className="h-4 bg-gray-200 rounded w-full"></div>
                    <div className="h-4 bg-gray-200 rounded w-full"></div>
                    <div className="h-4 bg-gray-200 rounded w-5/6"></div>
                    <div className="h-4 bg-gray-200 rounded w-4/6"></div>
                  </div>
                </div>

                {/* Section 2 */}
                <div>
                  <div className="h-6 w-40 bg-gray-200 rounded mb-4"></div>
                  <div className="bg-gray-50 rounded-lg p-6 space-y-4">
                    <div className="flex items-start space-x-3">
                      <div className="h-5 w-5 bg-gray-200 rounded"></div>
                      <div className="flex-1 space-y-2">
                        <div className="h-4 w-32 bg-gray-200 rounded"></div>
                        <div className="h-4 w-48 bg-gray-200 rounded"></div>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <div className="h-5 w-5 bg-gray-200 rounded"></div>
                      <div className="flex-1 space-y-2">
                        <div className="h-4 w-32 bg-gray-200 rounded"></div>
                        <div className="h-4 w-48 bg-gray-200 rounded"></div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Section 3 */}
                <div>
                  <div className="h-6 w-36 bg-gray-200 rounded mb-4"></div>
                  <div className="bg-blue-50 rounded-lg p-6 space-y-4">
                    <div className="flex items-start space-x-3">
                      <div className="h-5 w-5 bg-gray-200 rounded"></div>
                      <div className="flex-1 space-y-2">
                        <div className="h-4 w-32 bg-gray-200 rounded"></div>
                        <div className="h-4 w-40 bg-gray-200 rounded"></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Sidebar */}
              <div className="space-y-6">
                <div className="bg-gray-50 rounded-lg p-6 animate-pulse">
                  <div className="h-6 w-32 bg-gray-200 rounded mb-4"></div>
                  
                  <div className="space-y-4">
                    {[1, 2, 3, 4].map((i) => (
                      <div key={i} className="flex items-start space-x-3">
                        <div className="h-5 w-5 bg-gray-200 rounded"></div>
                        <div className="flex-1 space-y-2">
                          <div className="h-3 w-20 bg-gray-200 rounded"></div>
                          <div className="h-4 w-32 bg-gray-200 rounded"></div>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <div className="h-12 bg-gray-200 rounded-lg mb-4"></div>
                    <div className="h-24 bg-gray-200 rounded-lg"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
